import { createContext, ReactElement } from 'react';

import { PanelPosition } from './types';
import { PanelStateOptions } from './types/PanelStateOptions';

export interface PanelInstance {
  uniqueId: string;
  componentInstance: ReactElement;
  zIndex: number;
  poppedOut: boolean;
  window: Window | null;
  onClose?: () => void;
  hidden: boolean;
}

export interface OpenPanelOptions {
  uniqueId?: string;
  componentFn: () => ReactElement;
  poppedOut?: boolean;
  onClose?: () => void;
}

export interface PanelsContextType {
  openPanels: Set<string>;
  hasOpenPanel: (panelId: string) => boolean;
  // returns unique id of panel that was created/or already found, otherwise null on error.
  openPanel: (args: OpenPanelOptions) => string | null;
  closePanel: (uniqueId: string | string[]) => void;
  hidePanel: (uniqueId: string | string[]) => void;
  hideAllPanels: () => void;
  unhidePanel: (uniqueId: string | string[]) => void;
  unhideAllPanels: () => void;
  hasHiddenPanel: (uniqueId: string) => boolean;
  bringPanelToFront: (panelUniqueId: string) => void;
  nextStackPosition: () => PanelPosition;
  dockElements: Record<string, HTMLDivElement>;
  setDockElement: (id: string, element: HTMLDivElement) => void;
  panelStateOptions?: PanelStateOptions | null;
}

const NoContext = <T,>(): T => {
  console.error('Missing PanelContext Provider');
  return undefined as T;
};

export const PanelsContext = createContext<PanelsContextType>({
  openPanels: new Set(),
  hasOpenPanel: () => false,
  openPanel: NoContext,
  closePanel: NoContext,
  hidePanel: NoContext,
  hideAllPanels: NoContext,
  unhidePanel: NoContext,
  unhideAllPanels: NoContext,
  hasHiddenPanel: NoContext,
  bringPanelToFront: NoContext,
  dockElements: {},
  setDockElement: NoContext,
  nextStackPosition: () => ({ x: 0, y: 0 }),
});
